import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Autocomplete,
  Checkbox,
  FormControlLabel,
  Typography,
  CircularProgress,
} from "@mui/material";
import AddOutlined from "@mui/icons-material/AddOutlined";
import DeleteOutlined from "@mui/icons-material/DeleteOutlined";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { getServiceDimensionValues } from "slices/dimensions";
import { formFieldDimensionKey as dimensionKeyOf, hasServiceLookup } from "common/formFieldLookup";
import StateService from "../../services/state.service";


// Stable reference: `|| []` builds a new array per call, defeating
// react-redux's reference check and re-rendering on every dispatched action.
const EMPTY_DIMENSIONS = [];
/**
 * EditableFieldForm Component
 *
 * Handles "form" type fields that reference other state sets.
 * Fetches fields marked with inputByParent=true from the referenced state set
 * and displays them in a dialog for editing.
 * Stores the result as an object (single value) or array (multi value).
 */
const EditableFieldForm = ({
  field,
  initialValue,
  formReference, // ID of the referenced state set
  aliasName, // Optional display name used as dialog title
  isArray, // Whether to allow multiple form entries (reusing existing isArray field)
  onChange = () => {},
  setIsUpdating: _setIsUpdating, // Unused - kept for API compatibility
  onLoadingChange, // Callback to notify parent of loading state changes
  // Parent field's service configuration for default lookup
  serviceType,
  serviceImplemenator,
  serviceDimension,
  lookUpLabel,
  serviceFilter,
  isUnique = false, // Whether duplicate selections should be excluded
  readOnly = false, // Whether this is in read-only/detail mode
}) => {
  const dispatch = useDispatch();
  const [isEditing, setEditing] = useState(false);
  const [value, setValue] = useState(initialValue || (isArray ? [] : {}));
  const [currentFormData, setCurrentFormData] = useState({});
  // Start loading when there is a schema to fetch, so the first onLoadingChange report
  // is "loading" rather than a premature "done" that the parent then has to retract.
  const [isLoadingSchema, setIsLoadingSchema] = useState(Boolean(formReference));
  const [referencedSchema, setReferencedSchema] = useState(null);
  const [schemaError, setSchemaError] = useState(null);
  // Lookup keys this instance has requested. A ref, not state: recording a request must
  // not re-render or re-run the request effect.
  const requestedDimensionKeys = useRef(new Set());

  // Get all dimensions from Redux
  const dimensions = useSelector((state) => state.dimensions?.dimensions ?? EMPTY_DIMENSIONS);

  // Fetch the referenced state set UI schema on mount, sharing an in-flight request with
  // any table cells or other fields asking for the same one.
  useEffect(() => {
    if (!formReference) {
      setIsLoadingSchema(false);
      return undefined;
    }

    let cancelled = false;
    setIsLoadingSchema(true);
    setSchemaError(null);

    StateService.getStateUiShared(formReference)
      .then((response) => {
        if (!cancelled) setReferencedSchema(response);
      })
      .catch((error) => {
        console.error("Error fetching referenced state set schema:", error);
        if (!cancelled) setSchemaError(`Failed to load form schema for "${formReference}"`);
      })
      .finally(() => {
        if (!cancelled) setIsLoadingSchema(false);
      });

    return () => {
      cancelled = true;
    };
  }, [formReference]);

  // Extract fields where inputByParent === true
  const inputFields = React.useMemo(() => {
    if (!referencedSchema?.columns) return [];

    const fields = referencedSchema.columns
      .filter((field) => field.inputByParent === true)
      .sort((a, b) => (a.sequence || 0) - (b.sequence || 0));

    // If parent field has serviceType configured, add it as a default lookup field
    const allFields = [];

    if (serviceType && serviceImplemenator && serviceDimension) {
      // Create a default field for the parent's service lookup
      const defaultField = {
        field: serviceDimension, // Use serviceDimension as field name (e.g., "_id")
        headerName: aliasName || formReference, // Use aliasName if set, otherwise formReference
        uiType: "string",
        bsonType: "string",
        serviceType: serviceType,
        serviceImplemenator: serviceImplemenator,
        serviceDimension: serviceDimension,
        lookUpLabel: lookUpLabel,
        serviceFilter: serviceFilter,
        required: false,
        isArray: false,
        isUnique: isUnique,
        sequence: -1, // Put it first
      };
      allFields.push(defaultField);
    }

    // Add the inputByParent fields
    allFields.push(...fields);

    return allFields;
  }, [referencedSchema, serviceType, serviceImplemenator, serviceDimension, lookUpLabel, serviceFilter, formReference, aliasName, isUnique]);

  useEffect(() => {
    setValue(initialValue || (isArray ? [] : {}));
  }, [initialValue, isArray]);

  const handleEdit = () => {
    // Don't open edit dialog in read-only mode
    if (readOnly) return;

    setEditing(true);
    // Initialize form data with existing value if editing single object
    if (!isArray && typeof value === "object") {
      setCurrentFormData(value);
    } else {
      setCurrentFormData({});
    }
  };

  const handleCancel = () => {
    setEditing(false);
    setCurrentFormData({});
  };

  const handleSave = () => {
    let newValue;

    if (isArray) {
      // Add new entry to array
      newValue = [...value, { ...currentFormData }];
    } else {
      // Replace with new object
      newValue = { ...currentFormData };
    }

    setValue(newValue);
    onChange(field, newValue);
    setEditing(false);
    setCurrentFormData({});

    // Don't set isUpdating here - it causes the parent modal save button to show loading
    // The parent modal will handle the isUpdating state when the user clicks save
  };

  const handleFieldUpdate = (fieldName, newVal) => {
    setCurrentFormData((prev) => ({
      ...prev,
      [fieldName]: newVal,
    }));
  };

  /**
   * Get dimension options for dropdown fields
   */
  const getDimensionOptions = (item) => {
    const dimensionKey = dimensionKeyOf(item);
    const dimensionData = dimensions.find((d) => d.dimensionKey === dimensionKey);

    if (dimensionData?.values) {
      let options = dimensionData.values.map((v) => ({
        value: v.value,
        label: v.label || v.value,
      }));

      // If the field is marked as unique, exclude values already used in existing entries
      if (item.isUnique && isArray && Array.isArray(value)) {
        const usedValues = new Set(
          value.map((entry) => entry[item.field]).filter(Boolean)
        );
        options = options.filter((opt) => !usedValues.has(opt.value));
      }

      return options;
    }

    return [];
  };

  // Load lookups when editing OR when there's existing data to display
  const shouldLoadDimensions = Boolean(
    isEditing || (value && (!isArray || (Array.isArray(value) && value.length > 0)) && Object.keys(value).length > 0)
  );

  const lookupFields = React.useMemo(() => inputFields.filter(hasServiceLookup), [inputFields]);

  /**
   * Request each lookup once per mount. Deliberately not `ifMissing`: re-fetching when
   * the field mounts lets the dropdowns pick up records added since the lookup was first
   * loaded (e.g. a new Contributor).
   */
  useEffect(() => {
    if (!shouldLoadDimensions) return;

    lookupFields.forEach((item) => {
      const dimensionKey = dimensionKeyOf(item);
      if (requestedDimensionKeys.current.has(dimensionKey)) return;
      requestedDimensionKeys.current.add(dimensionKey);

      const search = {
        type: item.serviceType,
        implementor: item.serviceImplemenator,
        field: item.serviceDimension,
        lookUpLabel: item.lookUpLabel,
        filter: item.serviceFilter,
        dimensionKey
      };

      dispatch(getServiceDimensionValues({ search }));
    });
  }, [shouldLoadDimensions, lookupFields, dispatch]);

  // Derived from the store rather than kept as local state: loading while any lookup has
  // no entry yet or is still in flight. Failed and empty lookups settle with
  // loading=false, so this always clears once the requests finish.
  const isLoadingDimensions =
    shouldLoadDimensions &&
    lookupFields.some((item) => {
      const entry = dimensions.find((d) => d.dimensionKey === dimensionKeyOf(item));
      return !entry || entry.loading;
    });

  // Notify parent when initial loading completes
  useEffect(() => {
    // Notify about both schema and dimension loading for initial display
    const isLoading = isLoadingSchema || isLoadingDimensions;

    if (onLoadingChange) {
      onLoadingChange(field, isLoading);
    }
  }, [isLoadingSchema, isLoadingDimensions, field, onLoadingChange]);

  /**
   * Render input field based on field type
   */
  const renderInputField = (item) => {
    const currentValue = currentFormData[item.field] || "";
    const fieldType = item.uiType || item.bsonType;

    // For date type fields
    if (fieldType === "date") {
      return (
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <DatePicker
            value={currentValue ? moment(currentValue) : null}
            onChange={(newValue) => {
              handleFieldUpdate(
                item.field,
                newValue ? newValue.toISOString() : null
              );
            }}
            slotProps={{
              textField: {
                fullWidth: true,
                size: "small",
              },
            }}
          />
        </LocalizationProvider>
      );
    }

    // For boolean type fields
    if (fieldType === "boolean") {
      return (
        <FormControlLabel
          control={
            <Checkbox
              checked={currentValue === true || currentValue === "true"}
              onChange={(e) => handleFieldUpdate(item.field, e.target.checked)}
            />
          }
          label={currentValue ? "Yes" : "No"}
        />
      );
    }

    // For number type fields
    if (fieldType === "number") {
      return (
        <TextField
          fullWidth
          type="number"
          value={currentValue ?? ""}
          onChange={(e) => {
            const numValue = parseFloat(e.target.value);
            handleFieldUpdate(
              item.field,
              isNaN(numValue) ? "" : numValue
            );
          }}
          size="small"
          variant="outlined"
        />
      );
    }

    // For string type with service lookup (dropdown)
    if (fieldType === "string" && hasServiceLookup(item)) {
      const options = getDimensionOptions(item);

      if (item.isArray) {
        // Multi-select for array fields
        return (
          <Autocomplete
            multiple
            options={options}
            getOptionLabel={(option) => option.label || option.value || option}
            value={
              Array.isArray(currentValue)
                ? options.filter((opt) => currentValue.includes(opt.value))
                : []
            }
            onChange={(_event, newValue) => {
              handleFieldUpdate(
                item.field,
                newValue.map((v) => v.value)
              );
            }}
            renderInput={(params) => (
              <TextField {...params} variant="outlined" size="small" />
            )}
          />
        );
      } else {
        // Single select for non-array fields
        return (
          <Autocomplete
            options={options}
            getOptionLabel={(option) => option.label || option.value || option}
            value={
              options.find((opt) => opt.value === currentValue) || null
            }
            onChange={(_event, newValue) => {
              handleFieldUpdate(item.field, newValue ? newValue.value : null);
            }}
            renderInput={(params) => (
              <TextField {...params} variant="outlined" size="small" />
            )}
          />
        );
      }
    }

    // Default: string type with text field
    return (
      <TextField
        fullWidth
        value={currentValue}
        onChange={(e) => handleFieldUpdate(item.field, e.target.value)}
        size="small"
        variant="outlined"
        multiline={item.multiLine || (item.rows && item.rows > 1)}
        rows={item.rows || 1}
        inputProps={
          item.maxLength && item.maxLength > 0
            ? { maxLength: item.maxLength }
            : undefined
        }
      />
    );
  };

  const handleRemoveItem = (index) => {
    if (isArray) {
      const newValue = value.filter((_, i) => i !== index);
      setValue(newValue);
      onChange(field, newValue);
      // Don't set isUpdating here - the delete is synchronous and doesn't need loading state
      // Setting it to true without ever resetting to false would hang the parent's save button
    }
  };

  if (!formReference) {
    return (
      <Typography color="error">
        Form reference not configured
      </Typography>
    );
  }

  // Get display value for a field (label instead of value ID)
  const getDisplayValue = (fieldName, fieldValue) => {
    if (fieldValue === undefined || fieldValue === null) {
      return "-";
    }

    // Find the field schema
    const fieldSchema = inputFields.find((f) => f.field === fieldName);

    if (!fieldSchema) {
      return String(fieldValue);
    }

    // If it's a dropdown field, try to get the label
    if (hasServiceLookup(fieldSchema)) {
      const dimensionKey = dimensionKeyOf(fieldSchema);
      const dimensionData = dimensions.find((d) => d.dimensionKey === dimensionKey);

      if (dimensionData?.values) {
        const option = dimensionData.values.find((v) => v.value === fieldValue);
        if (option) {
          return option.label || option.value;
        }
      }
    }

    return String(fieldValue);
  };

  // Render table for array values
  const renderTable = () => {
    if (!isArray || !Array.isArray(value) || value.length === 0) {
      return null;
    }

    // Get all unique keys from all entries
    const allKeys = new Set();
    value.forEach((entry) => {
      Object.keys(entry).forEach((key) => allKeys.add(key));
    });
    const columns = Array.from(allKeys);

    // Get header names for columns
    const getHeaderName = (fieldName) => {
      if (aliasName && columns.length === 1) return aliasName;
      const fieldSchema = inputFields.find((f) => f.field === fieldName);
      return fieldSchema?.headerName || fieldSchema?.uiHeader || fieldName;
    };

    return (
      <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell key={col}>{getHeaderName(col)}</TableCell>
              ))}
              {/* Only show Actions column if not in read-only mode */}
              {!readOnly && <TableCell align="right">Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {value.map((row, index) => (
              <TableRow key={index}>
                {columns.map((col) => (
                  <TableCell key={col}>
                    {getDisplayValue(col, row[col])}
                  </TableCell>
                ))}
                {/* Only show delete button if not in read-only mode */}
                {!readOnly && (
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      aria-label="Delete row"
                      onClick={() => handleRemoveItem(index)}
                      color="error"
                    >
                      <DeleteOutlined fontSize="small" />
                    </IconButton>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  // Render current value display for non-array form fields
  const renderCurrentValue = () => {
    // Only show for non-array fields with existing values
    if (isArray || !value || (typeof value === 'object' && Object.keys(value).length === 0)) {
      return null;
    }

    // Get header names for columns
    const entries = Object.entries(value);
    const getHeaderName = (fieldName) => {
      if (aliasName && entries.length === 1) return aliasName;
      const fieldSchema = inputFields.find((f) => f.field === fieldName);
      return fieldSchema?.headerName || fieldSchema?.uiHeader || fieldName;
    };

    return (
      <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Field</TableCell>
              <TableCell>Value</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Object.entries(value).map(([key, val]) => (
              <TableRow key={key}>
                <TableCell sx={{ fontWeight: 600 }}>
                  {getHeaderName(key)}
                </TableCell>
                <TableCell>
                  {getDisplayValue(key, val)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Box>
      {renderTable()}
      {renderCurrentValue()}

      {!readOnly && (
        <Button
          variant="outlined"
          startIcon={<AddOutlined />}
          onClick={handleEdit}
          size="small"
          disabled={isLoadingSchema || isLoadingDimensions}
        >
          {isArray ? "Add Entry" : "Edit"}
        </Button>
      )}

      <Dialog
        open={isEditing}
        onClose={handleCancel}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {aliasName || (isArray ? "Add New Entry" : "Edit Form Data")}
          <Typography variant="caption" display="block" color="text.secondary">
            {formReference}
          </Typography>
        </DialogTitle>

        <DialogContent dividers>
          {isLoadingSchema || isLoadingDimensions ? (
            <Box display="flex" justifyContent="center" alignItems="center" py={4}>
              <CircularProgress />
              <Typography ml={2}>
                {isLoadingSchema ? "Loading form schema..." : "Loading data..."}
              </Typography>
            </Box>
          ) : schemaError ? (
            <Box py={2}>
              <Typography color="error">{schemaError}</Typography>
            </Box>
          ) : inputFields.length === 0 ? (
            <Box py={2}>
              <Typography color="warning.main">
                No fields marked as &quot;Input by parent&quot; in referenced form
              </Typography>
            </Box>
          ) : (
            <Box display="flex" flexDirection="column" gap="1.5rem">
              {inputFields.map((inputField) => (
                <Box key={inputField.field}>
                  <Typography
                    variant="body2"
                    fontWeight="600"
                    color="text.primary"
                    mb={0.5}
                  >
                    {inputField.headerName || inputField.uiHeader || inputField.field}
                    {inputField.required && (
                      <Typography
                        component="span"
                        color="error"
                        ml={0.5}
                      >
                        *
                      </Typography>
                    )}
                  </Typography>

                  {inputField.helperText && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      mb={0.5}
                    >
                      {inputField.helperText}
                    </Typography>
                  )}

                  {renderInputField(inputField)}
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ padding: 2 }}>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            {isArray ? "Add Entry" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EditableFieldForm;
